// TODO(ui.4-followup): date-range filter, multi-value event_type filter, and
// substring search over event_id are deferred — backend GET /v1/events accepts
// only single event_type + livemode (see wh_v1_events.go). The event_id input
// here is a strict-match getEvent lookup.

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Form, Spinner } from "react-bootstrap";
import { FiEye, FiExternalLink, FiSearch, FiX } from "react-icons/fi";
import moment from "moment";
import { listEvents, getEvent } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import { EVENTS } from "../eventCatalogue";
import EventPayloadDrawer from "./eventPayloadDrawer";

const PAGE_LIMIT = 50;

const WIRED_EVENTS = EVENTS.filter((e) => !e.notWired);

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm:ss") : "—");

const LivemodeBadge = ({ livemode }) => (
  <span className={`badge bg-${livemode ? "primary" : "info"}`}>
    {livemode ? "live" : "test"}
  </span>
);

const EventsTab = ({ appPubID }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useContext(ToastrContext);

  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  // Filters
  const [eventType, setEventType] = useState("");
  const [livemode, setLivemode] = useState("all"); // all | live | test

  // Strict-match lookup
  const [lookupInput, setLookupInput] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Drawer
  const [drawerEvent, setDrawerEvent] = useState(null);

  const load = async (after = null) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      if (after) params.starting_after = after;
      if (eventType) params.event_type = eventType;
      if (livemode === "live") params.livemode = "true";
      else if (livemode === "test") params.livemode = "false";
      const res = await listEvents(params);
      const items = Array.isArray(res.data) ? res.data : [];
      setEvents((prev) => (after ? [...prev, ...items] : items));
      setHasMore(!!res.has_more);
      setCursor(res.next_cursor || null);
    } catch (e) {
      showError((e && e.message) || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters change (apply-on-change)
  useEffect(() => {
    setEvents([]);
    setCursor(null);
    setHasMore(false);
    setLookupResult(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPubID, eventType, livemode]);

  const doLookup = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const val = lookupInput.trim();
    if (!val) return;
    if (!val.startsWith("evt_")) {
      showError("Use the full evt_… id");
      return;
    }
    setLookupLoading(true);
    try {
      const res = await getEvent(val);
      setLookupResult(res);
    } catch (err) {
      setLookupResult(null);
      showError((err && err.message) || "Event not found");
    } finally {
      setLookupLoading(false);
    }
  };

  const clearLookup = () => {
    setLookupInput("");
    setLookupResult(null);
  };

  const findDeliveries = (ev) => {
    // Carry event_id to the Webhooks tab via search query; preserve everything
    // else (path is relative to /developers/:appPubID).
    navigate({
      pathname: location.pathname,
      search: `?event_id=${encodeURIComponent(ev.event_id)}`,
      hash: "#webhooks",
    });
  };

  const rows = lookupResult ? [lookupResult] : events;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-0">Events</h5>
          <p className="text-muted mb-0 small">
            Raw workspace event log. Click a row to inspect the full payload, or
            jump to its webhook deliveries.
          </p>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1">Event type</label>
          <Form.Select
            size="sm"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            disabled={!!lookupResult}
          >
            <option value="">(all)</option>
            {WIRED_EVENTS.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2">
          <label className="form-label small text-muted mb-1">Livemode</label>
          <Form.Select
            size="sm"
            value={livemode}
            onChange={(e) => setLivemode(e.target.value)}
            disabled={!!lookupResult}
          >
            <option value="all">All</option>
            <option value="live">Live only</option>
            <option value="test">Test only</option>
          </Form.Select>
        </div>
        <div className="col-md-7">
          <label className="form-label small text-muted mb-1">
            Look up by event ID
          </label>
          <form onSubmit={doLookup} className="d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm font-monospace"
              placeholder="evt_…"
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
            />
            <Button
              type="submit"
              variant="outline-primary"
              size="sm"
              disabled={lookupLoading || !lookupInput.trim()}
            >
              {lookupLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FiSearch className="align-middle me-1" />
                  Look up
                </>
              )}
            </Button>
            {lookupResult && (
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={clearLookup}
                title="Clear lookup"
              >
                <FiX />
              </Button>
            )}
          </form>
        </div>
      </div>

      <div className="table-responsive">
        {loading && rows.length === 0 ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <p className="mb-0">No events match the current filters.</p>
            <p className="small mb-0">
              Events fire automatically as messages, sessions, and bots run —
              trigger one from the chat side to see it appear here.
            </p>
          </div>
        ) : (
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Type</th>
                <th>Livemode</th>
                <th>Device</th>
                <th>Created</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((ev) => (
                <tr key={ev.id || ev.event_id}>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link p-0 font-monospace"
                      onClick={() => setDrawerEvent(ev)}
                      title="View payload"
                    >
                      {ev.event_id}
                    </button>
                  </td>
                  <td>
                    <code>{ev.event_type}</code>
                  </td>
                  <td>
                    <LivemodeBadge livemode={ev.livemode} />
                  </td>
                  <td>{ev.idt_device != null ? ev.idt_device : "—"}</td>
                  <td>{formatTs(ev.ts_created)}</td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => setDrawerEvent(ev)}
                      title="View payload"
                    >
                      <FiEye className="align-middle me-1" />
                      Payload
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => findDeliveries(ev)}
                      title="Find webhook deliveries for this event"
                    >
                      <FiExternalLink className="align-middle me-1" />
                      Find deliveries
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {hasMore && !lookupResult && (
          <div className="text-center">
            <Button
              variant="outline-secondary"
              onClick={() => load(cursor)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <EventPayloadDrawer
        event={drawerEvent}
        onClose={() => setDrawerEvent(null)}
      />
    </div>
  );
};

export default EventsTab;
